import { useState, useEffect } from 'react'
import { fmtUsdt, fmtKrw } from '@/utils/currency'
import { fmtUtcToKstDate } from '@/utils/dateKst'   // partners.created_at 은 UTC 저장 (2026-10-06)
import { useTranslation } from 'react-i18next'
import { partnerApiService } from '@/services/partnerApiService'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import {
  Hash,
  Percent,
  Users,
  TrendingUp,
  DollarSign,
  Copy,
  Wallet,
  Activity,
  ArrowDownRight,
  ArrowUpRight,
  Building2,
  UserCheck,
  Link2
} from 'lucide-react'
import { toast } from 'react-hot-toast'

interface PartnerInfo {
  id: number
  name: string
  username: string
  referral_code?: string
  commission_rate?: number
  settlement_mode?: string  // DEPOSIT=죽장 / PNL=손익 (2026-07-24 타입 보강)
  parent_partner_id?: number | null
  partner_level?: number
  sub_partner_count?: number
  created_at: string
}

interface PartnerStats {
  // 직접 추천 유저 통계
  direct_users_count: number
  active_users_count: number

  // 유저 재정 현황
  total_user_deposits: number
  total_user_withdrawals: number
  total_user_balance: number
  direct_user_net_loss: number

  // 하위 파트너 유저 통계
  sub_partner_user_count: number
  sub_partner_net_loss: number
  sub_partner_earnings: number

  // 수익 현황
  direct_gross_earnings: number
  total_gross_earnings: number
  total_withdrawn: number
  available_balance: number
}

interface UserStats {
  user_id: number
  email: string
  nickname: string
  total_deposited: number
  total_withdrawn: number
  current_balance: number
  net_loss: number
}

const PartnerDashboard = () => {
  const [partnerInfo, setPartnerInfo] = useState<PartnerInfo | null>(null)
  const isPnl = partnerInfo?.settlement_mode === 'PNL'  // 손익 모드 — 라벨 분기 (2026-07-22)
  const [stats, setStats] = useState<PartnerStats | null>(null)
  const [balBreak, setBalBreak] = useState<any | null>(null)   // 산하 원화/USDT 잔고 분해 (2026-08-31)
  const [fxRate, setFxRate] = useState<number | null>(null)     // 표시용 환율 (원화 병기)
  const [userStats, setUserStats] = useState<UserStats[]>([])
  const [loading, setLoading] = useState(true)
  const { t } = useTranslation('dashboard')

  useEffect(() => {
    loadPartnerData()
  }, [])

  const loadPartnerData = async () => {
    try {
      setLoading(true)

      console.log('Loading partner dashboard data...')
      const dashboardData = await partnerApiService.getDashboard()
      console.log('Dashboard data:', dashboardData)

      setPartnerInfo(dashboardData.partner_info)
      setStats(dashboardData.stats)
      setBalBreak((dashboardData as any).balance_breakdown || null)
      setFxRate(Number((dashboardData as any).fx_rate) || null)
      setUserStats(dashboardData.user_stats || [])

    } catch (error) {
      console.error('Dashboard data load error:', error)
      toast.error(t('common:dataLoadFailed'))
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = async (text: string, successMessage: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text)
        toast.success(successMessage)
        return
      }

      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()

      const successful = document.execCommand('copy')
      document.body.removeChild(textArea)

      if (successful) {
        toast.success(successMessage)
      } else {
        throw new Error('Copy failed')
      }
    } catch (error) {
      console.error('Copy error:', error)
      toast.error(t('common:copyFailed'))
    }
  }

  const copyReferralCode = () => {
    if (partnerInfo?.referral_code) {
      copyToClipboard(partnerInfo.referral_code, t('referralCodeCopied'))
    }
  }

  const copyReferralUrl = () => {
    if (partnerInfo?.referral_code) {
      const url = `https://kryzonic.com/?ref=${partnerInfo.referral_code}`
      copyToClipboard(url, t('referralLinkCopied'))
    }
  }

  const krwNote = (usd: number) => fxRate ? `≈ ${fmtKrw(usd * fxRate)}` : ''
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner w-8 h-8" />
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <Helmet>
        <title>{t('pageTitle')}</title>
      </Helmet>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">{t('title')}</h1>
          <p className="text-dark-400 mt-1">{t('subtitle')}</p>
        </div>
      </motion.div>

      {/* Partner Info Card */}
      {partnerInfo && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card p-4 sm:p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-medium text-lg">
                  {partnerInfo.name.charAt(0)}
                </span>
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-white">{partnerInfo.name}</h2>
                <p className="text-dark-400">{t('partnerLevel', { level: partnerInfo.partner_level || 1 })}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-dark-400">{t('joinDate')}</p>
              <p className="text-white">{fmtUtcToKstDate(partnerInfo.created_at)}</p>
            </div>
          </div>

          {partnerInfo.referral_code ? (
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <p className="text-sm text-dark-300 font-medium">{t('referralCode')}</p>
                <div className="flex items-center space-x-2">
                  <span className="text-lg font-mono text-white bg-primary-500/20 px-3 py-2 rounded-lg border border-primary-500/30">
                    {partnerInfo.referral_code}
                  </span>
                  <button
                    onClick={copyReferralCode}
                    className="glass-button p-2 rounded-lg"
                    title={t('copyCode')}
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={copyReferralUrl}
                    className="glass-button p-2 rounded-lg"
                    title={t('copyLink')}
                  >
                    <Link2 className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-dark-500">
                  {t('referralLinkDisplay', { code: partnerInfo.referral_code })}
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-dark-300 font-medium">{t('commissionRate')}</p>
                <div className="flex items-center space-x-2">
                  <Percent className="w-5 h-5 text-green-400" />
                  <span className="text-lg font-semibold text-green-400">
                    {partnerInfo.commission_rate}%
                  </span>
                </div>
                <p className="text-xs text-dark-500">{((partnerInfo as any).settlement_mode === 'PNL') ? t('commissionDescriptionPnl') : t('commissionDescription')}</p>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-dark-300 font-medium">{t('subPartners')}</p>
                <div className="flex items-center space-x-2">
                  <Building2 className="w-5 h-5 text-blue-400" />
                  <span className="text-lg font-semibold text-blue-400">
                    {partnerInfo.sub_partner_count || 0}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Hash className="w-12 h-12 text-dark-600 mx-auto mb-4" />
              <p className="text-dark-400 mb-2">{t('noReferralCode')}</p>
              <p className="text-sm text-dark-500">{t('contactAdmin')}</p>
            </div>
          )}
        </motion.div>
      )}

      {/* 산하 잔고 원화/USDT 분해 (2026-08-31 — 관리자 대시보드式) */}
      {balBreak && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="glass-card p-4">
            <p className="text-dark-400 text-sm mb-1">{t('subBalanceUsdt')} <span className="text-dark-500">({t('holdersCount', { count: balBreak.usdt?.holders ?? 0 })})</span></p>
            <p className="text-xl font-bold text-white tabular-nums">{fmtUsdt(balBreak.usdt?.balance)}</p>
            <p className="text-xs text-dark-500 mt-1">{t('bonusLabel')} {fmtUsdt(balBreak.usdt?.bonus)}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }} className="glass-card p-4">
            <p className="text-dark-400 text-sm mb-1">{t('subBalanceKrw')} <span className="text-dark-500">({t('holdersCount', { count: balBreak.krw?.holders ?? 0 })})</span></p>
            <p className="text-xl font-bold text-amber-300 tabular-nums">{fmtKrw(balBreak.krw?.balance)}</p>
            <p className="text-xs text-dark-500 mt-1">{t('bonusLabel')} {fmtKrw(balBreak.krw?.bonus)}</p>
          </motion.div>
        </div>
      )}

      {/* Key Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 lg:gap-4">
          {/* Available Balance */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card p-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-dark-400 text-sm">{t('availableBalance')}</p>
                <p className={`text-xl font-bold ${stats.available_balance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {formatCurrency(stats.available_balance)}
                </p>
                {fxRate && <p className="text-xs text-amber-300/80 mt-0.5 tabular-nums">{krwNote(stats.available_balance)}</p>}
                <p className="text-xs text-dark-500 mt-1">{t('withdrawable')}</p>
              </div>
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Wallet className="w-5 h-5 text-green-400" />
              </div>
            </div>
          </motion.div>

          {/* Total Earnings */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="glass-card p-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-dark-400 text-sm">{t('totalEarnings')}</p>
                <p className={`text-xl font-bold ${stats.total_gross_earnings >= 0 ? 'text-primary-400' : 'text-red-400'}`}>
                  {formatCurrency(stats.total_gross_earnings)}
                </p>
                {fxRate && <p className="text-xs text-amber-300/80 mt-0.5 tabular-nums">{krwNote(stats.total_gross_earnings)}</p>}
                <p className="text-xs text-dark-500 mt-1">{t('beforeWithdrawal')}</p>
                {(Number((stats as any).transfer_carry) || 0) !== 0 && (
                  <p className="text-[11px] text-violet-300 mt-0.5">{t('transferCarryNote', { amount: formatCurrency((stats as any).transfer_carry) })}</p>
                )}
              </div>
              <div className="w-10 h-10 bg-primary-500/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-primary-400" />
              </div>
            </div>
          </motion.div>

          {/* Total Withdrawn */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-card p-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-dark-400 text-sm">{t('totalWithdrawn')}</p>
                <p className="text-xl font-bold text-yellow-400">
                  {formatCurrency(stats.total_withdrawn)}
                </p>
                {fxRate && <p className="text-xs text-amber-300/80 mt-0.5 tabular-nums">{krwNote(stats.total_withdrawn)}</p>}
                <p className="text-xs text-dark-500 mt-1">{t('cumulative')}</p>
              </div>
              <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                <ArrowUpRight className="w-5 h-5 text-yellow-400" />
              </div>
            </div>
          </motion.div>

          {/* Direct Users */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="glass-card p-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-dark-400 text-sm">{t('directReferralUsers')}</p>
                <p className="text-xl font-bold text-blue-400">
                  {stats.direct_users_count}
                </p>
                <p className="text-xs text-dark-500 mt-1">{t('activeCount', { count: stats.active_users_count })}</p>
              </div>
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* User Financial Summary */}
      {stats && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="glass-card p-4 sm:p-6"
        >
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <Activity className="w-5 h-5" />
            <span>{t('userFinancialStatus')}</span>
          </h3>
          {isPnl && (
            <p className="text-xs text-dark-500 -mt-2 mb-4">{t('pnlCardNote')}</p>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-dark-800/50 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <ArrowDownRight className="w-4 h-4 text-green-400" />
                <p className="text-sm text-dark-400">{t('totalDeposits')}</p>
              </div>
              <p className="text-lg font-bold text-white">{formatCurrency(stats.total_user_deposits)}</p>
            </div>

            <div className="p-4 bg-dark-800/50 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <ArrowUpRight className="w-4 h-4 text-red-400" />
                <p className="text-sm text-dark-400">{t('totalWithdrawals')}</p>
              </div>
              <p className="text-lg font-bold text-white">{formatCurrency(stats.total_user_withdrawals)}</p>
            </div>

            <div className="p-4 bg-dark-800/50 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Wallet className="w-4 h-4 text-blue-400" />
                <p className="text-sm text-dark-400">{t('currentBalance')}</p>
              </div>
              <p className="text-lg font-bold text-white">{formatCurrency(stats.total_user_balance)}</p>
            </div>

            <div className="p-4 bg-dark-800/50 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <DollarSign className="w-4 h-4 text-yellow-400" />
                <p className="text-sm text-dark-400">{t(isPnl ? 'userNetLossPnl' : 'userNetLoss')}</p>
              </div>
              <p className={`text-lg font-bold ${stats.direct_user_net_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {formatCurrency(stats.direct_user_net_loss)}
              </p>
            </div>
          </div>

          {/* Sub Partner Stats */}
          {(stats.sub_partner_user_count > 0 || stats.sub_partner_earnings > 0) && (
            <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <h4 className="text-sm font-medium text-blue-400 mb-3">{t('subPartnerUserEarnings')}</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-dark-400">{t('userCount')}</p>
                  <p className="text-lg font-bold text-white">{stats.sub_partner_user_count}</p>
                </div>
                <div>
                  <p className="text-xs text-dark-400">{t(isPnl ? 'netLossPnl' : 'netLoss')}</p>
                  <p className="text-lg font-bold text-white">{formatCurrency(stats.sub_partner_net_loss)}</p>
                </div>
                <div>
                  <p className="text-xs text-dark-400">{t('myEarnings')}</p>
                  <p className={`text-lg font-bold ${stats.sub_partner_earnings >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatCurrency(stats.sub_partner_earnings)}</p>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* User List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="glass-card"
      >
        <div className="p-4 sm:p-6 border-b border-white/10">
          <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
            <UserCheck className="w-5 h-5" />
            <span>{t('referredUsers')}</span>
          </h3>
        </div>
        <div className="p-4 sm:p-6">
          {userStats.length > 0 ? (
            <>
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-dark-400 border-b border-white/10">
                      <th className="text-left py-3 px-3">{t('userColumn')}</th>
                      <th className="text-right py-3 px-3">{t('depositAmount')}</th>
                      <th className="text-right py-3 px-3">{t('withdrawalAmount')}</th>
                      <th className="text-right py-3 px-3">{t('balance')}</th>
                      <th className="text-right py-3 px-3">{t(isPnl ? 'netLossColumnPnl' : 'netLossColumn')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userStats.map((user) => (
                      <tr key={user.user_id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="py-3 px-3">
                          <div>
                            <p className="text-white font-medium">{user.nickname || 'N/A'}</p>
                            <p className="text-xs text-dark-400">{user.email}</p>
                          </div>
                        </td>
                        <td className="text-right py-3 px-3 text-green-400">
                          {formatCurrency(user.total_deposited)}
                        </td>
                        <td className="text-right py-3 px-3 text-red-400">
                          {formatCurrency(user.total_withdrawn)}
                        </td>
                        <td className="text-right py-3 px-3 text-white">
                          {formatCurrency(user.current_balance)}
                        </td>
                        <td className={`text-right py-3 px-3 font-medium ${user.net_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {formatCurrency(user.net_loss)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden divide-y divide-white/10">
                {userStats.map((user) => (
                  <div key={user.user_id} className="py-4 first:pt-0 last:pb-0">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2 min-w-0">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold text-xs">
                            {(user.nickname || 'N')[0]}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-white font-medium text-sm truncate">{user.nickname || 'N/A'}</p>
                          <p className="text-xs text-dark-400 truncate">{user.email}</p>
                        </div>
                      </div>
                      <span className={`text-sm font-bold flex-shrink-0 ${user.net_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatCurrency(user.net_loss)}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-dark-800/50 rounded-lg p-2 text-center">
                        <p className="text-[10px] text-dark-400">{t('depositAmount')}</p>
                        <p className="text-xs font-medium text-green-400">{formatCurrency(user.total_deposited)}</p>
                      </div>
                      <div className="bg-dark-800/50 rounded-lg p-2 text-center">
                        <p className="text-[10px] text-dark-400">{t('withdrawalAmount')}</p>
                        <p className="text-xs font-medium text-red-400">{formatCurrency(user.total_withdrawn)}</p>
                      </div>
                      <div className="bg-dark-800/50 rounded-lg p-2 text-center">
                        <p className="text-[10px] text-dark-400">{t('balance')}</p>
                        <p className="text-xs font-medium text-white">{formatCurrency(user.current_balance)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-dark-600 mx-auto mb-4" />
              <p className="text-dark-400">{t('noReferredUsers')}</p>
              <p className="text-sm text-dark-500 mt-2">{t('shareReferralCode')}</p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  )
}

export default PartnerDashboard
